import { FC, useMemo } from "react";
import Button from "../ui/Button";
import DeleteModal from "../ui/DeleteModal";
import { IEntityChangeLogDetail } from "../../interfaces/endpoints/IEntityChangeLog.types";
import { revertEntityChange } from "../../services/EntityChangeLog.service";
import { PARENT_COLUMN } from "./changeHistoryDictionary";
import {
  ACTION_LABELS,
  actionLabel,
  actionStyle,
  describeActor,
  formatDisplayValue,
  formatUtc,
  isHiddenColumn,
  labelForEntity,
  parseChanges,
  parseState,
  revertParagraph
} from "./changeHistoryLabels";
import { revertBlockedSentence, revertWarningSentence } from "./revertText";
import { inReadingOrder } from "./changeSummary";
import { ChangesTable, StateList } from "./ReadableChanges";
import TechnicalDetails from "./TechnicalDetails";

interface IChangeDetailsBody {
  row: IEntityChangeLogDetail;
  onReverted: () => void;
}

// One history row for a reader: what changed in plain words, how the entity looked before, and the
// revert. Ids, request ids and raw JSON stay available under "Të dhënat teknike".
const ChangeDetailsBody: FC<IChangeDetailsBody> = ({ row, onReverted }) => {
  const changes = useMemo(
    () => inReadingOrder(Object.entries(parseChanges(row.changes)).filter(([c]) => !isHiddenColumn(c))),
    [row.changes]
  );
  const previous = useMemo(
    () =>
      inReadingOrder(
        Object.entries(parseState(row.previousState)).filter(([c]) => c !== "Id" && !isHiddenColumn(c))
      ),
    [row.previousState]
  );
  const format = (column: string, value: unknown) =>
    formatDisplayValue(row.entityName, column, value, row.references);
  const parentColumn = PARENT_COLUMN[row.entityName];
  const parentName =
    row.parentEntityDisplay ??
    row.references.find((r) => r.column === parentColumn && r.id === row.parentEntityId)?.display;
  // What the undo does not reach goes into the confirmation; why it is refused, where the button was.
  const warning = revertWarningSentence(row.revertWarningCode);
  const paragraph = `${revertParagraph()} ${warning}`.trim();
  const blocked = revertBlockedSentence(row.revertBlockedCode, row.revertBlockedReason, row.newerChanges);
  const isCreated = row.action === "Created";

  return (
    <>
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 mb-5">
        <dt className="text-gray-500">Veprimi</dt>
        <dd>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${actionStyle(row)}`}>
            {actionLabel(row)}
          </span>
        </dd>
        <dt className="text-gray-500">Kush</dt>
        <dd>{describeActor(row)}</dd>
        <dt className="text-gray-500">Kur</dt>
        <dd>{formatUtc(row.changedAtUtc)}</dd>
        {row.revertOf && (
          <>
            <dt className="text-gray-500">Kthen</dt>
            <dd>
              ndryshimin e {formatUtc(row.revertOf.changedAtUtc)} ({ACTION_LABELS[row.revertOf.action]})
            </dd>
          </>
        )}
        {row.parentEntityName && row.parentEntityId && (
          <>
            <dt className="text-gray-500">I përket</dt>
            <dd title={`${row.parentEntityName} #${row.parentEntityId}`}>
              {labelForEntity(row.parentEntityName)} {parentName ?? `#${row.parentEntityId}`}
            </dd>
          </>
        )}
      </dl>

      {changes.length > 0 && (
        <>
          <h4 className="font-semibold mb-2">{isCreated ? "Vlerat fillestare" : "Çfarë u ndryshua"}</h4>
          <ChangesTable
            entityName={row.entityName}
            changes={changes}
            format={format}
            createdOnly={isCreated}
          />
        </>
      )}

      {previous.length > 0 && (
        <details className="mb-5">
          <summary className="cursor-pointer font-semibold">Si ishte para ndryshimit</summary>
          <StateList entityName={row.entityName} entries={previous} format={format} />
        </details>
      )}

      <TechnicalDetails row={row} />

      <div className="flex items-center justify-between gap-4 border-t border-gray-200 pt-4">
        <p className="text-gray-500">{row.canRevert ? paragraph : blocked}</p>
        {row.canRevert && (
          <DeleteModal
            params={row.id}
            handleDelete={revertEntityChange}
            title="Konfirmo kthimin"
            paragraph={paragraph}
            successMessage="Ndryshimi u kthye mbrapa"
            errorMessage="Kthimi dështoi"
            loadingMessage="Duke kthyer..."
            indicator={<Button className="whitespace-nowrap">Kthe mbrapa</Button>}
            onSuccess={onReverted}
          />
        )}
      </div>
    </>
  );
};

export default ChangeDetailsBody;
